<template>
  <div class="testing-layout">
    <div class="testing-main">
      <div class="progress-bar-wrap">
        <div class="progress-meta">
          <span>{{ currentIndex + 1 }} / {{ questions.length }}</span>
          <span>{{ quiz.name }}</span>
        </div>
        <div class="progress-track">
          <div
            class="progress-fill"
            :style="{ width: progressPercent + '%' }"
          />
        </div>
      </div>

      <transition name="slide" mode="out-in">
        <div
          :key="currentQuestion.id"
          class="question-card"
        >
          <div class="question-number">Question {{ currentIndex + 1 }}</div>
          <h2 class="question-text">{{ currentQuestion.question_text }}</h2>
          <div class="options-list">
            <div
              v-for="opt in currentQuestion.options"
              :key="opt.id"
              class="option-card"
              :class="{
                selected: answers[String(currentQuestion.id)] === opt.id,
                disabled: locked
              }"
              @click="onSelect(currentQuestion.id, opt.id)"
            >
              <span class="option-letter">{{ optionLetter(opt) }}</span>
              <span class="option-text">{{ opt.option_text }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TestingPage',
  props: {
    quiz: { type: Object, required: true },
    questions: { type: Array, default: () => [] },
    currentIndex: { type: Number, default: 0 },
    answers: { type: Object, default: () => ({}) },
    locked: { type: Boolean, default: false }
  },
  computed: {
    currentQuestion () {
      return this.questions[this.currentIndex] || {}
    },
    progressPercent () {
      if (!this.questions.length) return 0
      return ((this.currentIndex + 1) / this.questions.length) * 100
    }
  },
  methods: {
    onSelect (questionId, optionId) {
      if (this.locked) return
      this.$emit('select-option', questionId, optionId)
    },
    optionLetter (opt) {
      const idx = this.currentQuestion.options.indexOf(opt)
      return String.fromCharCode(65 + idx)
    }
  }
}
</script>

<style scoped>
.testing-layout {
  display: flex;
  justify-content: center;
  padding: 2rem 1rem 3rem;
  min-height: 80vh;
  align-items: flex-start;
}

.testing-main {
  flex: 1;
  max-width: 680px;
}

.progress-bar-wrap {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 12px rgba(13,31,60,0.06);
}

.progress-meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  color: #6b7fa0;
  margin-bottom: 0.75rem;
}

.progress-track {
  height: 6px;
  background: #e8edf5;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #1a3a6b, #c9a227);
  border-radius: 3px;
  transition: width 0.4s ease;
}

.question-card {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 24px rgba(13,31,60,0.08);
  animation: fadeSlide 0.35s ease;
}

.question-number {
  font-size: 0.75rem;
  font-weight: 600;
  color: #c9a227;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
}

.question-text {
  font-family: 'Source Serif 4', serif;
  font-size: 1.15rem;
  color: #0d1f3c;
  line-height: 1.65;
  margin-bottom: 1.75rem;
}

.options-list { display: flex; flex-direction: column; gap: 0.75rem; }

.option-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1.5px solid #e8edf5;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafbfd;
}

.option-card:hover {
  border-color: #1a3a6b;
  background: #f0f4fa;
}

.option-card.selected {
  border-color: #1a3a6b;
  background: linear-gradient(135deg, #f0f4fa, #e8edf5);
  box-shadow: 0 2px 12px rgba(26,58,107,0.1);
}

.option-card.disabled {
  pointer-events: none;
  opacity: 0.85;
}

.option-letter {
  width: 28px;
  height: 28px;
  background: #1a3a6b;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}

.option-card.selected .option-letter {
  background: #c9a227;
  color: #0d1f3c;
}

.option-text {
  font-size: 0.925rem;
  color: #2a4060;
  line-height: 1.5;
  padding-top: 0.2rem;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.28s ease;
}

.slide-enter { opacity: 0; transform: translateX(24px); }
.slide-leave-to { opacity: 0; transform: translateX(-24px); }

@media (max-width: 640px) {
  .question-card { padding: 1.25rem; }
}
</style>
